/**
 * buildComponentWritePayload
 *
 * Converts the full Formik / API-response draft into the minimal, safe payload
 * that the API's strict componentSchema accepts on create and update.
 *
 * Implements: C1 (allow-list), C2 (per-type data sanitization),
 *             C-N (null = unset pass-through), C-F (filter transform),
 *             C-cat (drop invalid categoryId).
 *
 * C-N note: optional fields are allowed to carry `null` so that Admin can
 * signal "unset this field" to the API. Once the API-side C13b normalization
 * lands (null → undefined → $unset), this pass-through can be tightened so
 * the log-gate goes fully clean before C14 enforcement.
 */

import { componentsConfiguration, ALLOWED_OPTIONS_KEYS } from './configuration';
import { resolveDataSourceMode } from './DataSource/resolveDataSourceMode';

import type {
  ComponentInput,
  ComponentOptions,
  ComponentFilter,
  ComponentDataSource,
} from '../../types';
import type { Filter } from '../FiltersPicker/types';

// ─── types ───────────────────────────────────────────────────────────────────

/**
 * The exact shape sent to the API on create / update.
 * All fields are optional so the same type works for both create and PATCH.
 * `image` is included to pass through the top-level `scheduled` image field
 * whose schema modelling is deferred to API-side C13 (it is already in the
 * @vgtv/api-client contract).
 */
export type ComponentWritePayload = Partial<
  Pick<
    ComponentInput,
    | 'type'
    | 'isShared'
    | 'sharedName'
    | 'categoryId'
    | 'header'
    | 'content'
    | 'editor'
    | 'options'
    | 'overrides'
    | 'filters'
    | 'data'
  >
> & {
  /** Top-level image used by `scheduled` components (API contract: @vgtv/api-client). */
  image?: string | null;
};

// ─── helpers ─────────────────────────────────────────────────────────────────

/**
 * C-cat: drop categoryId when it is not a positive integer.
 * Accepts a `number` or a numeric `string` (the `CategoriesSelect` picker
 * supplies categoryId as a string), coercing strings to a validated number.
 */
function sanitizeCategoryId(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return undefined;
  }
  const id = Number(value);
  if (!Number.isInteger(id) || id <= 0) {
    return undefined;
  }
  return id;
}

/**
 * C-F (corrected): the Formik FiltersPicker emits
 *   { type: 'tag', tag: { id: string, title: string } }
 *   { type: 'story', story: { id: string, title: string } }
 *   { type: 'category', category: { id: string, title: string } }
 *
 * The API componentFilterSchema expects: { type, entityId }
 * where category entityId is a number, tag/story entityId is a string.
 */
function transformFilter(filter: Filter): ComponentFilter | null {
  const entity = (filter as Record<string, unknown>)[filter.type] as
    { id: string } | undefined;
  if (!entity?.id) {
    return null;
  }
  if (filter.type === 'category') {
    const id = Number(entity.id);
    if (!Number.isFinite(id) || id <= 0) return null;
    return { type: 'category', entityId: id };
  }
  if (filter.type === 'tag') {
    return { type: 'tag', entityId: entity.id };
  }
  if (filter.type === 'story') {
    return { type: 'story', entityId: entity.id };
  }
  return null;
}

/** C5: strip any option key not in the 11-key whitelist (e.g. hasSingleItem). */
function sanitizeOptions(
  options: ComponentOptions | null | undefined
): ComponentOptions | undefined {
  if (options == null) return undefined;
  const entries = ALLOWED_OPTIONS_KEYS.filter((key) => key in options).map(
    (key) => [key, options[key]] as const
  );
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries) as ComponentOptions;
}

/**
 * C2: sanitize the `data` field for a given component type.
 *
 * - filtered-list / scheduled: omit data entirely.
 * - otherwise: keep only { type, source, preset2 }; drop preset (resolved blob)
 *   and url (server-computed). Also drop source when endpoint is undefined,
 *   and drop preset2 / source when the dataType has no preset/endpoints in
 *   configuration (e.g. seasons).
 *
 * NOTE: this function operates on the *raw Formik values*, so data.preset is
 * the resolved array of blobs — it is always dropped. preset2 is derived in
 * handleSubmit (C7) before buildComponentWritePayload is called.
 */
function sanitizeData(
  componentType: string,
  data: Record<string, unknown> | undefined
): ComponentInput['data'] | undefined {
  const filterOnlyTypes = ['filtered-list', 'scheduled'];
  if (filterOnlyTypes.includes(componentType)) {
    return undefined;
  }

  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const dataType = data.type as string | undefined;
  if (!dataType) {
    // no data.type → drop a typeless/empty data object (API-side normalization
    // also drops these, but better to not send them at all)
    return undefined;
  }

  // Look up the configuration entry for this component type + data type
  const componentConfig =
    componentsConfiguration.dataSources[
      componentType as keyof typeof componentsConfiguration.dataSources
    ];
  const entry = componentConfig
    ? (
        componentConfig as ReadonlyArray<{
          dataType: string;
          endpoints?: readonly string[];
          preset?: boolean;
        }>
      ).find((e) => e.dataType === dataType)
    : null;

  const hasPreset = Boolean(entry?.preset);
  const hasEndpoints = Boolean(entry?.endpoints && entry.endpoints.length > 0);

  // Build the sanitized source
  let source: ComponentDataSource | undefined;
  if (hasEndpoints && data.source && typeof data.source === 'object') {
    const rawSource = data.source as Record<string, unknown>;
    if (rawSource.endpoint !== undefined) {
      source = {
        endpoint: rawSource.endpoint as ComponentDataSource['endpoint'],
        params: rawSource.params as Record<string, unknown> | undefined,
      };
    }
    // if endpoint is undefined, source is dropped (husk from DataSource.tsx)
  }

  // Build preset2 — only if this dataType supports a preset. In 'preset'
  // mode it is the mapped id array; in 'endpoint' mode it is `[]` (see NOTE
  // below); for non-preset-capable types (e.g. seasons) it stays omitted.
  // `data.dataSourceMode` is a transient client-only field and is never
  // copied into the result below.
  const rawPreset2 = data.preset2;
  const hasPresetItems =
    (Array.isArray(data.preset) && data.preset.length > 0) ||
    (Array.isArray(rawPreset2) && rawPreset2.length > 0);
  const mode = resolveDataSourceMode({
    hasEndpoints,
    hasPreset,
    storedMode: data.dataSourceMode,
    hasPresetItems,
  });
  // NOTE: for preset-capable types in 'endpoint' mode, preset2 must be an
  // explicit empty array — not omitted. The API merges
  // `incoming.preset2 ?? existing.preset2`, so omitting the field is read
  // as "keep the existing preset", while `[]` is the only way to actually
  // clear a previously-stored preset.
  const preset2: string[] | undefined = !hasPreset
    ? undefined
    : mode === 'preset' && Array.isArray(rawPreset2) && rawPreset2.length > 0
      ? (rawPreset2 as string[])
      : [];

  // Build the result as a plain object then cast to the discriminated union.
  // The discriminator ensures seasons never carries source/preset2 (they are
  // both undefined for seasons since entry has no preset/endpoints).
  const result: Record<string, unknown> = { type: dataType };
  if (source !== undefined) result.source = source;
  if (preset2 !== undefined) result.preset2 = preset2;

  return result as ComponentInput['data'];
}

// ─── public API ──────────────────────────────────────────────────────────────

/**
 * Build the minimal, schema-safe write payload from a Formik component draft.
 * Called in createComponent and updateComponent after C7 (explicit data build).
 *
 * @param draft - the full Formik values / API response shape
 */
export function buildComponentWritePayload(
  draft: Record<string, unknown>
): ComponentWritePayload {
  const componentType = draft.type as string | undefined;

  // C-cat: strip invalid categoryId
  const rawCategoryId = draft.categoryId;
  const categoryId =
    rawCategoryId === null || rawCategoryId === undefined
      ? undefined
      : sanitizeCategoryId(rawCategoryId);

  // C-F: transform filters from form shape to API shape
  const rawFilters = draft.filters;
  const filters: ComponentFilter[] | undefined =
    Array.isArray(rawFilters) && rawFilters.length > 0
      ? (rawFilters as Filter[])
          .map(transformFilter)
          .filter((f): f is ComponentFilter => f !== null)
      : undefined;

  // C5: strip disallowed options keys (e.g. hasSingleItem)
  const options = sanitizeOptions(
    draft.options as ComponentOptions | null | undefined
  );

  // C2: sanitize data per component type
  const data = sanitizeData(
    componentType ?? '',
    draft.data as Record<string, unknown> | undefined
  );

  // C-N: overrides — pass through as-is (including null values for unset).
  // TODO(C13b): once the API-side null→unset normalization lands, strip null
  // values here so that the Datadog log-gate goes fully clean before C14.
  const overrides =
    (draft.overrides as ComponentWritePayload['overrides']) ?? undefined;

  // Build the 12-key allow-list payload (plus image for scheduled)
  const payload: ComponentWritePayload = {};

  if (componentType !== undefined)
    payload.type = componentType as ComponentWritePayload['type'];
  if (draft.isShared !== undefined)
    payload.isShared = draft.isShared as boolean;
  if (draft.sharedName !== undefined)
    payload.sharedName = draft.sharedName as string | undefined;
  if (categoryId !== undefined) payload.categoryId = categoryId;
  if (draft.header !== undefined)
    payload.header = draft.header as string | undefined;
  if (draft.content !== undefined)
    payload.content = draft.content as string | undefined;
  if (draft.editor !== undefined)
    payload.editor = draft.editor as string | undefined;
  if (options !== undefined) payload.options = options;
  if (overrides !== undefined) payload.overrides = overrides;
  if (filters !== undefined) payload.filters = filters;
  if (data !== undefined) payload.data = data;

  // Pass through top-level `image` for `scheduled` components.
  // The field is in the @vgtv/api-client contract; schema modelling deferred to
  // API-side C13. C-N applies here too (null = unset signal).
  if (componentType === 'scheduled' && 'image' in draft) {
    payload.image = draft.image as string | null | undefined;
  }

  return payload;
}
